import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { LibraryPanesModule } from '@app/library/panes/library-panes.module';
import { SubjectItemComponent } from '@app/library/panes/subjects-content-filter/subject-item/subject-item.component';
import { CLibraryFilterData } from '@app/store/models';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AccountService, ContentService } from '@core/services';

import { IconComponent } from '@shared/icon/icon.component';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { getMe } from '@store/reducers/profile.reducer';

import {
  EContentPaneFormat,
  EContentPanelRoute,
  EntityTypeEnum,
  IContentFilterBlock,
  Subject as BaseSubject,
  SubjectCategoryEnum,
  User,
} from 'lingo2-models';
import { filter, takeUntil } from 'rxjs/operators';
import { AbstractContentPane } from '../models/abstract-content-pane';

enum EFilter {
  all,
  foreign,
  native,
  others,
}

@Component({
  selector: 'app-subjects-content-filter',
  templateUrl: './subjects-content-filter.component.html',
  styleUrls: ['./subjects-content-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,
    ScrollingModule,
    LibraryPanesModule,
    RouterModule,
    IconComponent,

    SubjectItemComponent,
  ],
})
export class SubjectsContentFilterComponent extends AbstractContentPane implements OnInit {
  private readonly contentService = inject(ContentService);
  И;
  @ViewChild('scroller', { read: CdkVirtualScrollViewport, static: false }) scroller: CdkVirtualScrollViewport;

  public itemSize = 166;
  public isDragScrollEnabled: boolean;
  public baseUrl: string;
  public filterActive: EFilter = EFilter.all;
  public eFilter = EFilter;
  public eContentPaneFormat = EContentPaneFormat;
  public baseRoute: EContentPanelRoute;

  private _me: User;
  private _libraryFilterData: CLibraryFilterData;

  ngOnInit() {
    this.isDragScrollEnabled = this.deviceService.isDesktop();
    // Выключено, потому что страницы /subjects не готова `${EContentPanelRoute.lessons}/subjects`;
    this.blockTitle.see_all_link = '';

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => (this._me = me),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.store
      .select(getLibraryRouteData)
      .pipe(filter((x) => !!x))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (libraryFilterData) => {
          this._libraryFilterData = libraryFilterData;
          this.baseRoute = EContentPanelRoute[libraryFilterData.baseRoute];
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  onMakeFavorite(panel: IContentFilterBlock) {
    panel.is_favorite = !panel.is_favorite;
    this.contentService
      .setBookmark(panel.id.toString(), panel.is_favorite, EntityTypeEnum.subject)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  hasContent(): boolean {
    return this._pane?.filters?.length > 0;
  }

  protected onContentLoaded(viaQuery?: boolean) {
    this.isVisible = true;
  }

  public routeLinkByItem(panel: IContentFilterBlock) {
    this.libRouter.setSubject((panel.item as BaseSubject).code);
    return this.libRouter.getLink();
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + this.itemSize * 3, 'smooth');
  }

  public filterSubjects() {
    switch (this.filterActive) {
      case EFilter.foreign:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.foreign_language,
        );
      case EFilter.native:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.native_language,
        );
      case EFilter.others:
        return this.filterPanels.filter(
          (_s: IContentFilterBlock) => _s.item['category_id'] === SubjectCategoryEnum.non_categorized,
        );
      default:
        return this.filterPanels || [];
    }
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  public trackSubjectByFn(index, item: BaseSubject | IContentFilterBlock): number {
    return item.id;
  }
}
